import { useEffect, useState } from 'react';

import { storeUnreadBellAlerts } from 'actions/bellAlerts';
import { getBellAlerts, markAsRead } from 'actions/user';
import { sortBellAlertsByTime } from 'components/bellAlerts/helpers';
import {
  ALL_BELL_ALERTS,
  COMMON_BELL_ALERT,
  USER_BELL_ALERT,
} from 'components/bellAlerts/partials/constants';
import { IBellAlert, TCategoryValues } from 'components/bellAlerts/types';
// import notifications from 'components/Header/partials/BellAlerts/newData';
import { DELTA_INDIA_LANDING_SITE_ROOT, IS_INDIAN_EXCHANGE } from 'constants/constants';
import { getDateTime, getDiff, timeNow, toUTC } from 'helpers/day';
import useLocalStorage from 'hooks/useLocalStorage';
import i18n from 'i18n/config';
import { userRegistrationDateSelector } from 'selectors';
import { isUserIndianSelector } from 'selectors/otherSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';

interface IBellAlertFilter {
  category: TCategoryValues;
  categories: string[];
  unread: boolean;
}

type TResponseStatus = 'loading' | 'success' | 'error';

export type TUseBellAlerts = ReturnType<typeof useBellAlerts>;

const SIGN_UP_DELTA_INDIA_BELL_ALERT = {
  category: 'delta_news',
  cta_link: DELTA_INDIA_LANDING_SITE_ROOT,
  cta_text: i18n.t('account:bellAlerts.deltaIndiaCta'),
  description: null,
  end_date: '2030-10-19T08:55:00Z',
  id: 999,
  is_user_bell_alert: false,
  meta_data: {
    analytic_tag: 'Delta India Sign up Bell alert',
    description: `${i18n.t('account:bellAlerts.deltaIndiaDescription1')}
    ${i18n.t('account:bellAlerts.deltaIndiaDescription2')}
    ${i18n.t('account:bellAlerts.deltaIndiaDescription3')}
    ${i18n.t('account:bellAlerts.deltaIndiaDescription4', { leverage: '100' })}`,
  },
  read: true,
  start_date: '2024-04-18T12:25:00Z',
  title: i18n.t('account:bellAlerts.deltaIndiaTitle'),
  user_bell_alert_id: null,
};

const useBellAlerts = () => {
  const [allBellAlerts, setAllBellAlerts] = useState<IBellAlert[]>([]);
  const [displayBellAlerts, setDisplayBellAlerts] = useState<IBellAlert[]>([]);

  // there are two filters as of now read-unread and category wise filter
  const [bellAlertFilters, setBellAlertFilters] = useState<IBellAlertFilter>({
    unread: false,
    categories: [],
    category: ALL_BELL_ALERTS,
  });
  const [responseStatus, setResponseStatus] = useState<TResponseStatus>('loading');
  const [isNewBellAlerts, setIsNewBellAlerts] = useState(false);

  const userRegistrationDate = useAppSelector(userRegistrationDateSelector);

  const [lastActiveTime, setLastActiveTime] = useLocalStorage(
    '@BellAlerts/lastActiveTime',
    toUTC(timeNow())
  );

  const dispatch = useAppDispatch();

  const isUserIndian = useAppSelector(isUserIndianSelector);

  /**
   * @description This function filter out expired and outdated bell alerts
   * Expired Bell alerts - Whose expiry date is less than current date
   * Outdated bell alert - whose start date is less than user registration date
   * @param bellAlerts - list of bell alerts which get from API
   * @returns filtered bell alerts
   */
  function filterExpiredBellAlerts(bellAlerts: IBellAlert[]) {
    return bellAlerts.filter(bellAlert => {
      const expiryDifference = getDiff(
        toUTC(timeNow()),
        toUTC(getDateTime(bellAlert.end_date)),
        'milliseconds'
      );
      const outdatedDifference = getDiff(
        bellAlert.start_date,
        userRegistrationDate,
        'milliseconds'
      );
      return expiryDifference < 0 && outdatedDifference > 0;
    });
  }

  // get latest bell alerts by calling bell_alert API
  function getLatestBellAlerts() {
    dispatch(getBellAlerts())
      .then((response: { result: IBellAlert[] }) => {
        const selectedBellAlerts = sortBellAlertsByTime(response.result);
        // const selectedBellAlerts = sortBellAlertsByTime(notifications);
        const filteredBellAlerts = filterExpiredBellAlerts(selectedBellAlerts);
        setAllBellAlerts(filteredBellAlerts);
        setResponseStatus('success');
      })
      .catch(() => {
        setResponseStatus('error');
      });
  }

  function changeFilter(updatedFilter: Partial<IBellAlertFilter>) {
    setBellAlertFilters(prev => ({
      ...prev,
      ...updatedFilter,
    }));
  }

  useEffect(() => {
    getLatestBellAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Checks if there is any new bell Alert
  useEffect(() => {
    if (allBellAlerts.length > 0) {
      const userHasOpenedBellAlertsBefore = localStorage.getItem(
        '@BellAlerts/lastActiveTime'
      );
      if (userHasOpenedBellAlertsBefore) {
        const bellAlertTime = toUTC(getDateTime(allBellAlerts[0].start_date));
        const timeDifference = getDiff(bellAlertTime, lastActiveTime, 'milliseconds');
        setIsNewBellAlerts(timeDifference > 0);
      } else {
        setIsNewBellAlerts(true);
      }
    }
  }, [allBellAlerts, lastActiveTime]);

  // Apply active filters on bell Alerts
  useEffect(() => {
    const { unread, categories } = bellAlertFilters;
    let bellAlerts =
      isUserIndian && !IS_INDIAN_EXCHANGE
        ? [SIGN_UP_DELTA_INDIA_BELL_ALERT, ...allBellAlerts]
        : [...allBellAlerts];

    if (categories.length > 0) {
      bellAlerts = bellAlerts.filter(bellAlert =>
        categories.includes(bellAlert.category)
      );
    }

    if (unread) {
      bellAlerts = bellAlerts.filter(bellAlert => bellAlert.read === false);
    }

    setDisplayBellAlerts(bellAlerts);
  }, [bellAlertFilters, allBellAlerts, userRegistrationDate, isUserIndian]);

  // calculate all unread bell alerts
  useEffect(() => {
    if (allBellAlerts.length > 0) {
      let unreadBellAlertsCount = 0;
      allBellAlerts.forEach(bellAlert => {
        if (!bellAlert.read) {
          unreadBellAlertsCount += 1;
        }
      });
      dispatch(storeUnreadBellAlerts(unreadBellAlertsCount));
    }
  }, [allBellAlerts, dispatch]);

  // helper function
  function findBellAlertById(id: number, idType: string) {
    let filteredResult;
    if (idType === USER_BELL_ALERT) {
      filteredResult = displayBellAlerts.find(
        bellAlert => bellAlert.user_bell_alert_id === id
      );
    }
    if (idType === COMMON_BELL_ALERT) {
      filteredResult = displayBellAlerts.find(bellAlert => bellAlert.id === id);
    }
    return filteredResult;
  }

  function isRead(id: number, idType: string) {
    const bellAlertDetails = findBellAlertById(id, idType);
    return bellAlertDetails ? bellAlertDetails.read : true;
  }

  function handleToggle(propertyName: 'unread') {
    if (propertyName in bellAlertFilters) {
      changeFilter({ [propertyName]: !bellAlertFilters[propertyName] });
    }
  }

  function handleRead(id: number, idType: string) {
    let readApiRequest;

    if (idType === USER_BELL_ALERT) {
      readApiRequest = markAsRead([], [id]);
    } else {
      readApiRequest = markAsRead([id], []);
    }

    dispatch(readApiRequest)
      .then(() => {
        getLatestBellAlerts();
      })
      // eslint-disable-next-line no-console
      .catch((error: any) => console.log(error));
  }

  function handleReadAll() {
    const commonBellAlertIds: number[] = [];
    const userBellAlertIds: number[] = [];

    // distinguish between commonBellAlert and userBellAlert
    displayBellAlerts.forEach(displayBellAlert => {
      return displayBellAlert.is_user_bell_alert && displayBellAlert.user_bell_alert_id
        ? userBellAlertIds.push(displayBellAlert.user_bell_alert_id)
        : commonBellAlertIds.push(displayBellAlert.id);
    });

    dispatch(markAsRead(commonBellAlertIds, userBellAlertIds))
      .then(() => {
        getLatestBellAlerts();
      })
      // eslint-disable-next-line no-console
      .catch((error: any) => console.log(error));
  }

  return {
    isRead,
    handleRead,
    handleReadAll,
    handleToggle,
    changeFilter,
    lastActiveTime,
    isNewBellAlerts,
    setIsNewBellAlerts,
    setLastActiveTime,
    responseStatus,
    displayBellAlerts,
    allBellAlerts,
    bellAlertFilters,
  };
};
export default useBellAlerts;
